import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, MailCheck, KeyRound, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Logo } from "../components/Logo";

const detail = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
};

const Shell = ({ children }) => (
  <div className="mm-frame mm-glow relative flex flex-col px-6 pt-6 pb-10" data-testid="password-page">
    <div className="flex items-center justify-between">
      <Link to="/login" data-testid="pw-back-link" className="w-9 h-9 -ml-2 rounded-full flex items-center justify-center text-navy hover:bg-slate-100"><ChevronLeft size={22} /></Link>
      <Logo size={30} /><span className="w-9" />
    </div>
    <div className="flex-1 flex flex-col justify-center">{children}</div>
  </div>
);

const Icon = ({ children }) => (
  <div className="w-16 h-16 mx-auto mb-5 rounded-[24px] bg-white border border-slate-200 shadow-[0_12px_30px_-12px_rgba(37,99,235,.45)] flex items-center justify-center">
    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-royal to-turq text-white flex items-center justify-center">{children}</div>
  </div>
);

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post("/auth/forgot-password", { email }); setSent(true); }
    catch (err) { toast.error(detail(err)); } finally { setBusy(false); }
  };
  return (
    <Shell>
      <Icon><KeyRound size={22} /></Icon>
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <MailCheck size={28} className="mx-auto text-emerald-500 mb-3" />
          <h1 className="font-serif text-2xl font-bold text-navy">Check your email</h1>
          <p className="text-slate-500 text-sm mt-2 leading-relaxed">If <strong>{email}</strong> is registered, a reset link is on its way. It expires in 60 minutes.</p>
          <Link to="/login" data-testid="forgot-back-to-login" className="inline-block mt-6 text-royal font-semibold text-sm">Back to sign in</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-3" data-testid="forgot-form">
          <h1 className="font-serif text-2xl font-bold text-navy text-center">Forgot your password?</h1>
          <p className="text-slate-500 text-sm text-center mb-4">Enter your email and we'll send you a link to choose a new one.</p>
          <input data-testid="forgot-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
            className="w-full px-4 py-3.5 rounded-2xl bg-white border border-slate-300 focus:border-royal outline-none" />
          <button data-testid="forgot-submit-btn" disabled={busy} className="w-full bg-royal text-white font-bold py-3.5 rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 size={16} className="animate-spin" />} Send reset link
          </button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login", { replace: true });
    } catch (err) { toast.error(detail(err)); } finally { setBusy(false); }
  };
  if (!token) return <Shell><p className="text-center text-slate-500" data-testid="reset-missing-token">This reset link is incomplete. <Link to="/forgot-password" className="text-royal font-semibold">Request a new one</Link>.</p></Shell>;
  return (
    <Shell>
      <Icon><KeyRound size={22} /></Icon>
      <form onSubmit={submit} className="space-y-3" data-testid="reset-form">
        <h1 className="font-serif text-2xl font-bold text-navy text-center">Choose a new password</h1>
        <p className="text-slate-500 text-sm text-center mb-4">At least 6 characters. You'll be signed out everywhere else.</p>
        <input data-testid="reset-password-input" type="password" required minLength={6} value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password"
          className="w-full px-4 py-3.5 rounded-2xl bg-white border border-slate-300 focus:border-royal outline-none" />
        <input data-testid="reset-confirm-input" type="password" required minLength={6} value={pw2} onChange={(e) => setPw2(e.target.value)} placeholder="Confirm new password"
          className="w-full px-4 py-3.5 rounded-2xl bg-white border border-slate-300 focus:border-royal outline-none" />
        <button data-testid="reset-submit-btn" disabled={busy} className="w-full bg-royal text-white font-bold py-3.5 rounded-2xl disabled:opacity-50 flex items-center justify-center gap-2">
          {busy && <Loader2 size={16} className="animate-spin" />} Update password
        </button>
      </form>
    </Shell>
  );
}

export default ForgotPassword;
